import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, FolderOpen, LayoutTemplate, MoreHorizontal, X } from 'lucide-react';
import { MousePointer, Film, Layers, LayoutGrid, Printer, Mail } from 'lucide-react';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';

// Tiny haptic helper — no-op on unsupported browsers (iOS Safari, desktop).
const haptic = (ms = 8) => {
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch { /* noop */ }
};

/**
 * MobileTabBar — fixed bottom navigation, mobile-only (md:hidden).
 *
 * Behavior:
 *   - "Create" opens a slide-up bottom-sheet of 6 round category tiles (Canva-style).
 *   - "Your Designs" → /campaigns
 *   - "Templates"    → /templates
 *   - "More"         → /menu
 *
 * Active state derived from current pathname.
 */
const CATEGORIES = [
  { key: 'post',       label: 'Post',       hint: 'Single image',     icon: MousePointer, path: '/templates?type=post' },
  { key: 'reel',       label: 'Reel',       hint: 'Vertical video',   icon: Film,         path: '/reel-editor' },
  { key: 'story',      label: 'Story',      hint: '9:16 ephemeral',   icon: Layers,       path: '/templates?type=story' },
  { key: 'carousel',   label: 'Carousel',   hint: 'Multi-slide',      icon: LayoutGrid,   path: '/carousel-editor' },
  { key: 'print',      label: 'Print',      hint: 'Flyers, postcards',icon: Printer,      path: '/print-center' },
  { key: 'newsletter', label: 'Newsletter', hint: 'Email blasts',     icon: Mail,         path: '/templates?type=newsletter' },
];

const NAV_TABS = [
  { key: 'designs',   label: 'Your Designs', icon: FolderOpen,       path: '/campaigns', match: (p) => p.startsWith('/campaigns') },
  { key: 'templates', label: 'Templates',    icon: LayoutTemplate,   path: '/templates', match: (p) => p.startsWith('/templates') },
  { key: 'more',      label: 'More',         icon: MoreHorizontal,   path: '/menu',      match: (p) => p === '/menu' },
];

export const MobileTabBar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const handleCategoryClick = (path) => {
    haptic(6);
    setSheetOpen(false);
    // small delay so sheet's exit animation isn't interrupted by navigation
    setTimeout(() => navigate(path), 80);
  };

  const handleOpenSheet = () => {
    haptic(8);
    setSheetOpen(true);
  };

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-xl border-t border-gray-200"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)', fontFamily: 'Manrope, sans-serif' }}
        data-testid="mobile-tab-bar"
      >
        <div className="grid grid-cols-4 h-16">
          {/* Create — opens sheet */}
          <button
            onClick={() => setSheetOpen(true)}
            className="flex flex-col items-center justify-center gap-1 px-1 active:scale-95 transition-transform"
            data-testid="mobile-tab-create"
            aria-label="Create new design"
          >
            <span
              className="flex items-center justify-center h-9 w-9 rounded-full text-white shadow-sm"
              style={{ background: '#0B0C10' }}
            >
              <Plus className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="text-[10.5px] font-medium tracking-wide text-black">Create</span>
          </button>

          {NAV_TABS.map(({ key, label, icon: Icon, path, match }) => {
            const active = match(pathname);
            return (
              <button
                key={key}
                onClick={() => navigate(path)}
                className="flex flex-col items-center justify-center gap-1 px-1 active:scale-95 transition-transform"
                data-testid={`mobile-tab-${key}`}
              >
                <Icon
                  className={`h-[22px] w-[22px] transition-colors ${active ? 'text-black' : 'text-gray-400'}`}
                  strokeWidth={1.75}
                />
                <span className={`text-[10.5px] font-medium tracking-wide ${active ? 'text-black' : 'text-gray-400'}`}>
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Slide-up Create sheet */}
      <Drawer open={sheetOpen} onOpenChange={setSheetOpen}>
        <DrawerContent
          className="md:hidden bg-white border-0 max-h-[88vh]"
          data-testid="create-sheet"
        >
          <DrawerTitle className="sr-only">Create new design</DrawerTitle>

          {/* Header */}
          <div className="px-6 pt-2 pb-4 flex items-start justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mt-2">Create</p>
              <h2
                className="mt-1 text-3xl font-light leading-[1.05] tracking-tight text-black"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                What are we{' '}
                <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic' }}>making</span>?
              </h2>
            </div>
            <button
              onClick={() => setSheetOpen(false)}
              className="h-9 w-9 mt-2 flex items-center justify-center rounded-full hover:bg-gray-100 active:scale-95 transition-all"
              data-testid="create-sheet-close"
              aria-label="Close"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>

          {/* 6 round category tiles — staggered scale-in */}
          <AnimatePresence>
            {sheetOpen && (
              <motion.div
                key="tiles"
                className="px-6 pb-10 grid grid-cols-3 gap-x-3 gap-y-7"
                initial="hidden"
                animate="show"
                exit="hidden"
                variants={{
                  hidden: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
                  show:   { transition: { staggerChildren: 0.045, delayChildren: 0.06 } },
                }}
              >
                {CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <motion.button
                      key={cat.key}
                      onClick={() => handleCategoryClick(cat.path)}
                      whileTap={{ scale: 0.92 }}
                      variants={{
                        hidden: { opacity: 0, y: 14, scale: 0.85 },
                        show:   { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 380, damping: 24 } },
                      }}
                      className="flex flex-col items-center gap-2.5"
                      data-testid={`create-sheet-${cat.key}`}
                    >
                      <span
                        className="flex items-center justify-center h-[72px] w-[72px] rounded-full bg-[#0B0C10] text-white shadow-md ring-1 ring-black/5"
                        aria-hidden
                      >
                        <Icon className="h-7 w-7" strokeWidth={1.5} />
                      </span>
                      <span className="flex flex-col items-center">
                        <span
                          className="text-[13px] font-semibold tracking-tight text-black"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                          {cat.label}
                        </span>
                        <span className="text-[10.5px] text-gray-400 mt-0.5 leading-tight text-center">
                          {cat.hint}
                        </span>
                      </span>
                    </motion.button>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default MobileTabBar;
